/**
 * Raptor Predator Frontend - UI Store
 * 
 * Manages UI state including:
 * - Selection state (hovered, selected nodes/threats)
 * - Camera targets and zoom levels
 * - Sidebar/modal state
 * - Theme and accessibility settings
 * - Notifications and toasts
 */

import { create } from 'zustand';
import { devtools, subscribeWithSelector, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type { ViewType, Notification, ModalState, ZoomLevel, Vector3 } from '../types';

// ============================================================================
// Types
// ============================================================================

interface CameraTarget {
  position: Vector3;
  lookAt: Vector3;
  transition: boolean;
  duration: number;
}

interface SelectionState {
  hoveredNodeId: string | null;
  selectedNodeIds: Set<string>;
  highlightedNodeIds: Set<string>;
  dimmedNodeIds: Set<string>;
}

interface UIState {
  // View state
  currentView: ViewType;
  sidebarOpen: boolean;
  sidebarWidth: number;
  
  // Theme
  theme: 'dark' | 'light' | 'system';
  reducedMotion: boolean;
  highContrast: boolean;
  
  // 3D visualization state
  zoomLevel: ZoomLevel;
  cameraTarget: CameraTarget | null;
  showLabels: boolean;
  showEdges: boolean;
  showThreats: boolean;
  
  // Selection state (imperative, for 3D interaction)
  selection: SelectionState;
  
  // Modals
  modals: ModalState[];
  
  // Notifications
  notifications: Notification[];
  
  // Search
  globalSearchQuery: string;
  globalSearchOpen: boolean;
  
  // Loading overlay
  globalLoading: boolean;
  globalLoadingMessage: string | null;
  
  // Last interaction timestamp (for idle detection)
  lastInteraction: number;
}

interface UIActions {
  // View navigation
  setView: (view: ViewType) => void;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;
  setSidebarWidth: (width: number) => void;
  
  // Theme
  setTheme: (theme: UIState['theme']) => void;
  setReducedMotion: (reduced: boolean) => void;
  setHighContrast: (high: boolean) => void;
  
  // 3D visualization
  setZoomLevel: (level: ZoomLevel) => void;
  setCameraTarget: (target: CameraTarget | null) => void;
  focusOnNode: (nodeId: string) => void;
  toggleLabels: () => void;
  toggleEdges: () => void;
  toggleThreats: () => void;
  
  // Selection
  setHoveredNode: (nodeId: string | null) => void;
  selectNode: (nodeId: string, addToSelection?: boolean) => void;
  deselectNode: (nodeId: string) => void;
  clearSelection: () => void;
  setHighlightedNodes: (nodeIds: string[]) => void;
  setDimmedNodes: (nodeIds: string[]) => void;
  clearHighlights: () => void;
  
  // Modals
  openModal: (modal: Omit<ModalState, 'id'>) => string;
  closeModal: (id: string) => void;
  closeAllModals: () => void;
  
  // Notifications
  addNotification: (notification: Omit<Notification, 'id'>) => string;
  removeNotification: (id: string) => void;
  clearNotifications: () => void;
  
  // Search
  setGlobalSearch: (query: string) => void;
  toggleGlobalSearch: () => void;
  
  // Loading
  setGlobalLoading: (loading: boolean, message?: string | null) => void;
  
  // Interaction tracking
  recordInteraction: () => void;
}

// ============================================================================
// Initial State
// ============================================================================

const initialSelection: SelectionState = {
  hoveredNodeId: null,
  selectedNodeIds: new Set(),
  highlightedNodeIds: new Set(),
  dimmedNodeIds: new Set(),
};

const initialState: UIState = {
  currentView: 'dashboard',
  sidebarOpen:
    typeof window !== 'undefined'
      ? window.matchMedia('(min-width: 768px)').matches
      : true,
  sidebarWidth: 280,
  theme: 'dark',
  reducedMotion: false,
  highContrast: false,
  zoomLevel: 'globe',
  cameraTarget: null,
  showLabels: true,
  showEdges: true,
  showThreats: true,
  selection: initialSelection,
  modals: [],
  notifications: [],
  globalSearchQuery: '',
  globalSearchOpen: false,
  globalLoading: false,
  globalLoadingMessage: null,
  lastInteraction: Date.now(),
};

// ============================================================================
// Helper Functions
// ============================================================================

function generateId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

// ============================================================================
// Store Creation
// ============================================================================

export const useUIStore = create<UIState & UIActions>()(
  devtools(
    subscribeWithSelector(
      persist(
        immer((set, get) => ({
          ...initialState,
          
          // View navigation
          setView: (view) => { set((state) => {
            state.currentView = view;
            state.lastInteraction = Date.now();
          }); },
          
          toggleSidebar: () => { set((state) => {
            state.sidebarOpen = !state.sidebarOpen;
            state.lastInteraction = Date.now();
          }); },

          setSidebarOpen: (open) => { set((state) => {
            state.sidebarOpen = open;
            state.lastInteraction = Date.now();
          }); },
          
          setSidebarWidth: (width) => { set((state) => {
            state.sidebarWidth = Math.max(200, Math.min(500, width));
            state.lastInteraction = Date.now();
          }); },
          
          // Theme
          setTheme: (theme) => { set((state) => {
            state.theme = theme;
          }); },
          
          setReducedMotion: (reduced) => { set((state) => {
            state.reducedMotion = reduced;
          }); },
          
          setHighContrast: (high) => { set((state) => {
            state.highContrast = high;
          }); },
          
          // 3D visualization
          setZoomLevel: (level) => { set((state) => {
            state.zoomLevel = level;
            state.lastInteraction = Date.now();
          }); },
          
          setCameraTarget: (target) => { set((state) => {
            state.cameraTarget = target;
            state.lastInteraction = Date.now();
          }); },
          
          focusOnNode: (nodeId) => { set((state) => {
            // This sets up a camera target request
            // The actual position lookup happens in the 3D scene
            state.cameraTarget = {
              position: { x: 0, y: 0, z: 0 }, // Placeholder - 3D scene will compute
              lookAt: { x: 0, y: 0, z: 0 },
              transition: true,
              duration: 1000,
            };
            state.selection.selectedNodeIds = new Set([nodeId]);
            state.lastInteraction = Date.now();
          }); },
          
          toggleLabels: () => { set((state) => {
            state.showLabels = !state.showLabels;
            state.lastInteraction = Date.now();
          }); },
          
          toggleEdges: () => { set((state) => {
            state.showEdges = !state.showEdges;
            state.lastInteraction = Date.now();
          }); },
          
          toggleThreats: () => { set((state) => {
            state.showThreats = !state.showThreats;
            state.lastInteraction = Date.now();
          }); },
          
          // Selection
          setHoveredNode: (nodeId) => { set((state) => {
            state.selection.hoveredNodeId = nodeId;
          }); },
          
          selectNode: (nodeId, addToSelection = false) => { set((state) => {
            if (addToSelection) {
              state.selection.selectedNodeIds.add(nodeId);
            } else {
              state.selection.selectedNodeIds = new Set([nodeId]);
            }
            state.lastInteraction = Date.now();
          }); },
          
          deselectNode: (nodeId) => { set((state) => {
            state.selection.selectedNodeIds.delete(nodeId);
            state.lastInteraction = Date.now();
          }); },
          
          clearSelection: () => { set((state) => {
            state.selection.selectedNodeIds = new Set();
            state.lastInteraction = Date.now();
          }); },
          
          setHighlightedNodes: (nodeIds) => { set((state) => {
            state.selection.highlightedNodeIds = new Set(nodeIds);
          }); },
          
          setDimmedNodes: (nodeIds) => { set((state) => {
            state.selection.dimmedNodeIds = new Set(nodeIds);
          }); },
          
          clearHighlights: () => { set((state) => {
            state.selection.highlightedNodeIds = new Set();
            state.selection.dimmedNodeIds = new Set();
          }); },
          
          // Modals
          openModal: (modal) => {
            const id = generateId();
            set((state) => {
              state.modals.push({ ...modal, id });
            });
            return id;
          },
          
          closeModal: (id) => { set((state) => {
            state.modals = state.modals.filter(m => m.id !== id);
          }); },
          
          closeAllModals: () => { set((state) => {
            state.modals = [];
          }); },
          
          // Notifications
          addNotification: (notification) => {
            const id = generateId();
            set((state) => {
              state.notifications.push({ ...notification, id });
            });
            
            // Auto-remove after duration
            const duration = notification.duration ?? 5000;
            if (duration > 0) {
              setTimeout(() => {
                get().removeNotification(id);
              }, duration);
            }
            
            return id;
          },
          
          removeNotification: (id) => { set((state) => {
            state.notifications = state.notifications.filter(n => n.id !== id);
          }); },
          
          clearNotifications: () => { set((state) => {
            state.notifications = [];
          }); },
          
          // Search
          setGlobalSearch: (query) => { set((state) => {
            state.globalSearchQuery = query;
            state.lastInteraction = Date.now();
          }); },
          
          toggleGlobalSearch: () => { set((state) => {
            state.globalSearchOpen = !state.globalSearchOpen;
            if (state.globalSearchOpen) {
              state.globalSearchQuery = '';
            }
            state.lastInteraction = Date.now();
          }); },
          
          // Loading
          setGlobalLoading: (loading, message = null) => { set((state) => {
            state.globalLoading = loading;
            state.globalLoadingMessage = message;
          }); },
          
          // Interaction tracking
          recordInteraction: () => { set((state) => {
            state.lastInteraction = Date.now();
          }); },
        })),
        {
          name: 'raptor-ui-store',
          partialize: (state) => ({
            // Only persist preferences, not transient UI state
            theme: state.theme,
            reducedMotion: state.reducedMotion,
            highContrast: state.highContrast,
            sidebarWidth: state.sidebarWidth,
            showLabels: state.showLabels,
            showEdges: state.showEdges,
            showThreats: state.showThreats,
          }),
        }
      )
    ),
    { name: 'ui-store' }
  )
);

// ============================================================================
// Selectors
// ============================================================================

export const selectCurrentView = (state: UIState & UIActions) => state.currentView;
export const selectTheme = (state: UIState & UIActions) => state.theme;
export const selectSidebarOpen = (state: UIState & UIActions) => state.sidebarOpen;
export const selectZoomLevel = (state: UIState & UIActions) => state.zoomLevel;
export const selectSelectedNodeIds = (state: UIState & UIActions) => state.selection.selectedNodeIds;
export const selectHoveredNodeId = (state: UIState & UIActions) => state.selection.hoveredNodeId;
export const selectHighlightedNodeIds = (state: UIState & UIActions) => state.selection.highlightedNodeIds;
export const selectDimmedNodeIds = (state: UIState & UIActions) => state.selection.dimmedNodeIds;
export const selectNotifications = (state: UIState & UIActions) => state.notifications;
export const selectModals = (state: UIState & UIActions) => state.modals;
export const selectGlobalSearchOpen = (state: UIState & UIActions) => state.globalSearchOpen;
export const selectGlobalSearchQuery = (state: UIState & UIActions) => state.globalSearchQuery;
export const selectGlobalLoading = (state: UIState & UIActions) => ({
  loading: state.globalLoading,
  message: state.globalLoadingMessage,
});

// ============================================================================
// Selection State Accessor (for imperative 3D updates)
// ============================================================================

/**
 * Get selection state for imperative access in 3D scene.
 * This bypasses React re-renders.
 */
export function getSelectionState(): SelectionState {
  return useUIStore.getState().selection;
}

